import * as React from 'react';
import { FieldRenderProps } from 'react-final-form';
import { Field } from '../Field';
import { FormItem } from './FormItem';
import { VFFieldProps } from '../types';
import { StringInput } from '../../string/StringInput';
import { DATA_FORM_INPUT } from '../../common/data';

export type StringProps = VFFieldProps<
  React.ComponentProps<typeof StringInput>
>;
export const String = React.forwardRef<HTMLInputElement, StringProps>(
  (
    {
      name,
      validate,
      className,
      inputClassName,
      contentClassName,
      inlineValidation,
      label,
      onChange: customOnChange,
      onBlur: customOnBlur,
      required,
      disabled,
      readOnly,
      validateWarning,
      ...other
    },
    ref
  ) => {
    const prevSentValue = React.useRef<string>();

    return (
      <FormItem
        name={name}
        inlineValidation={inlineValidation}
        className={className}
        label={label}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        validateWarning={validateWarning}
        contentClassName={contentClassName}
      >
        <Field name={name} validate={validate}>
          {({
            input: { value, onChange, onBlur, name, ...nother },
            meta,
          }: FieldRenderProps<string>) => {
            const [displayValue, setDisplayValue] = React.useState<
              string | undefined
            >(value);

            React.useEffect(() => {
              if (value !== prevSentValue.current) {
                prevSentValue.current = value;
                setDisplayValue(value);
              }
            }, [value]);

            const handleChange = React.useCallback(
              (e: React.ChangeEvent<HTMLInputElement>) => {
                const untrimmedValue = e.target.value;
                setDisplayValue(untrimmedValue);

                let realVal: string | undefined = untrimmedValue?.trim?.();
                if (realVal === '') {
                  realVal = undefined;
                }
                // Store the trimmed value so the effect knows this came from user input
                prevSentValue.current = realVal;
                const rv = { target: { name, value: realVal } };
                customOnChange
                  ? customOnChange(realVal as any)
                  : onChange?.(rv);
              },
              [customOnChange, onChange, name]
            );

            const handleOnBlur = React.useCallback(
              (e: React.FocusEvent<HTMLInputElement>) => {
                const untrimmedValue = e.target.value;
                let realVal: string | undefined = untrimmedValue?.trim?.();
                if (realVal === '') {
                  realVal = undefined;
                }

                setDisplayValue(realVal);
                prevSentValue.current = realVal;
                // Ensure form state is trimmed on blur (handles initial values
                // with whitespace that the user never typed into)
                const rv = { target: { name, value: realVal } };
                customOnChange ? customOnChange(realVal as any) : onChange?.(rv);
                customOnBlur ? customOnBlur(e) : onBlur?.(e);
              },
              [customOnBlur, onBlur, customOnChange, onChange, name]
            );

            return (
              <StringInput
                {...DATA_FORM_INPUT(name)}
                className={inputClassName}
                // @ts-ignore
                ref={ref}
                {...nother}
                name={name}
                {...other}
                disabled={disabled || meta.submitting}
                readOnly={readOnly}
                onChange={handleChange}
                onBlur={handleOnBlur}
                value={displayValue ?? ''}
              />
            );
          }}
        </Field>
      </FormItem>
    );
  }
);
